import { Fragment } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { ReportBlock, ReportOutput } from "@/profitability/types";
import {
  bentoLayout,
  bentoOrder,
  isHeadlineFigure,
  isSupportingFigure,
} from "./bento-layout";
import { BlockSkeleton } from "./block-skeleton";
import { reportPanes } from "./report-panes";

const HEADLINES = 3;
const SUPPORTING = 5;
const CHIPS = [112, 132, 124, 108];

const Pane = ({
  span,
  children,
}: {
  span: string;
  children: React.ReactNode;
}) => (
  <Card className={cn("flex flex-col gap-0 py-0", span)}>
    <Stack align="start" gap={0} className="flex-none px-4 pt-3 pb-2">
      <Skeleton className="h-4 w-40" />
    </Stack>
    {children}
  </Card>
);

/**
 * A pane whose blocks are not known yet. One region, rather than a bento
 * this report may not have.
 */
const UnknownPaneSkeleton = () => (
  <Stack className="w-full" gap={3} align="stretch" aria-busy role="status">
    <span className="sr-only">Loading report</span>
    <Skeleton className="h-4 w-40" aria-hidden />
    <Skeleton className="h-72 w-full rounded-xl" aria-hidden />
  </Stack>
);

/**
 * The report's own shape while it loads.
 *
 * A run page that has not said which blocks it has holds a dashboard. A
 * template pane holds one region until the script's blocks are known, and
 * then {@link DeclaredPaneSkeleton} holds those.
 */
export const ReportSkeleton = ({ tabs = true }: { tabs?: boolean }) =>
  tabs ? <KnownReportSkeleton /> : <UnknownPaneSkeleton />;

const KnownReportSkeleton = () => (
  <Stack className="w-full" gap={0} align="stretch" aria-busy role="status">
    <span className="sr-only">Loading report</span>
    <Stack
      direction="row"
      align="center"
      gap={6}
      className="flex-none pt-2 pb-5"
      aria-hidden
    >
      {CHIPS.map((width, tab) => (
        <Skeleton key={tab} className="h-4" style={{ width }} />
      ))}
    </Stack>
    <Stack direction="row" align="center" gap={2} className="pb-3" aria-hidden>
      {CHIPS.map((width, chip) => (
        <Skeleton key={chip} className="h-9 rounded-full" style={{ width }} />
      ))}
    </Stack>
    <Stack
      gap={0}
      align="stretch"
      className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-12"
      aria-hidden
    >
      {Array.from({ length: HEADLINES }, (_, figure) => (
        <Pane key={figure} span="lg:col-span-4">
          <BlockSkeleton type="summary" />
        </Pane>
      ))}
      <Card className="flex flex-row flex-wrap items-stretch gap-0 overflow-hidden py-0 sm:col-span-2 lg:col-span-12">
        {Array.from({ length: SUPPORTING }, (_, figure) => (
          <Stack
            key={figure}
            gap={2}
            align="stretch"
            className="-ml-px min-w-0 flex-1 basis-[190px] border-l px-4 py-3"
          >
            <Skeleton className="h-3 w-24" />
            <BlockSkeleton type="summary" compact />
          </Stack>
        ))}
      </Card>
      <Pane span="sm:col-span-2 lg:col-span-4">
        <BlockSkeleton type="bar" />
      </Pane>
      <Pane span="sm:col-span-2 lg:col-span-8">
        <BlockSkeleton type="bar" />
      </Pane>
    </Stack>
  </Stack>
);

const SupportingSkeleton = ({ blocks }: { blocks: ReportBlock[] }) => (
  <Card className="flex flex-row flex-wrap items-stretch gap-0 overflow-hidden py-0 sm:col-span-2 lg:col-span-12">
    {blocks.map((block) => (
      <Stack
        key={block.id}
        gap={2}
        align="stretch"
        className="-ml-px min-w-0 flex-1 basis-[190px] border-l px-4 py-3"
      >
        <Skeleton className="h-3 w-24" />
        <BlockSkeleton type="summary" compact />
      </Stack>
    ))}
  </Card>
);

/**
 * One declared pane, in the bento its blocks take. Held from a period of
 * this agent already in hand, so a week that is still loading keeps this
 * report's figures, charts, and tables instead of a generic dashboard.
 */
export const DeclaredPaneSkeleton = ({
  output,
  pane,
}: {
  output: ReportOutput;
  pane?: string;
}) => {
  const shown = reportPanes(output).find((item) => item.id === pane);
  if (!shown) return <UnknownPaneSkeleton />;
  const ordered = bentoOrder(shown.blocks);
  const fitsOneRow =
    ordered.filter((block) => block.type === "summary").length <= 4;
  const placed = fitsOneRow
    ? ordered.map((block) =>
        isSupportingFigure(block)
          ? { ...block, emphasis: "headline" as const }
          : block,
      )
    : ordered;
  const supporting = placed.filter(isSupportingFigure);
  const blocks = placed.filter((block) => !isSupportingFigure(block));
  const headlines = blocks.filter(isHeadlineFigure).length;
  const layout = bentoLayout(blocks, output.datasets);
  const drawn = new Set(shown.blocks.map((block) => block.dataset));
  const controls = output.controls.filter((control) =>
    drawn.has(control.dataset),
  );
  const strip = supporting.length > 0 && (
    <SupportingSkeleton blocks={supporting} />
  );
  return (
    <Stack className="w-full" gap={0} align="stretch" aria-busy role="status">
      <span className="sr-only">Loading report</span>
      {controls.length > 0 && (
        <Stack
          direction="row"
          align="center"
          gap={2}
          className="pb-3"
          aria-hidden
        >
          {controls.map((control) => (
            <Skeleton key={control.id} className="h-9 w-28 rounded-full" />
          ))}
        </Stack>
      )}
      <Stack
        gap={0}
        align="stretch"
        className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-12"
        aria-hidden
      >
        {!headlines && strip}
        {blocks.map((block, index) => (
          <Fragment key={block.id}>
            {block.type === "summary" ? (
              <Stack
                gap={2}
                className={cn("min-w-0", layout.get(block.id)?.span)}
              >
                <Skeleton className="h-4 w-20" />
                <BlockSkeleton type="summary" />
              </Stack>
            ) : (
              <Pane span={layout.get(block.id)?.span ?? "lg:col-span-12"}>
                <BlockSkeleton type={block.type} />
              </Pane>
            )}
            {!!headlines && index + 1 === headlines && strip}
          </Fragment>
        ))}
      </Stack>
    </Stack>
  );
};
